/**
 * Step-up prompt registry.
 *
 * A backend route with requireRecentOtp refuses with 403 STEP_UP_REQUIRED
 * when the user did not verify an OTP recently. The API client then calls
 * `requestStepUp`, which opens the registered prompt (the OTP dialog). When
 * the prompt resolves true, the API client sends the refused request again.
 *
 * This module imports nothing, so the API client and the dialog can both
 * import it without a cycle.
 */

export const STEP_UP_REQUIRED_CODE = "STEP_UP_REQUIRED";

/** Resolves true when the user verified the OTP, false when they cancelled. */
export type StepUpPrompt = () => Promise<boolean>;

let prompt: StepUpPrompt | null = null;
let pending: Promise<boolean> | null = null;

/**
 * Called by the OTP dialog while it is mounted. Returns the function that
 * unregisters it.
 */
export function registerStepUpPrompt(fn: StepUpPrompt): () => void {
	prompt = fn;
	return () => {
		if (prompt === fn) prompt = null;
	};
}

export function hasStepUpPrompt(): boolean {
	return prompt !== null;
}

/**
 * Opens the prompt, or resolves false when no prompt is mounted. Requests
 * that are refused while the prompt is open share that one prompt.
 */
export function requestStepUp(): Promise<boolean> {
	if (!prompt) return Promise.resolve(false);
	if (!pending) {
		pending = prompt().finally(() => {
			pending = null;
		});
	}
	return pending;
}
